import { computed, reactive, watchEffect, toRefs, isVNode, Fragment, Comment } from 'vue';

import { IN_BROWSER } from './dom/globals';

import type { TItemKey } from '@components/composable';
import type {
  ComponentPublicInstance,
  ComponentInternalInstance,
  ComputedGetter,
  InjectionKey,
  PropType,
  Ref,
  ToRefs,
  VNodeProps,
  VNodeChild,
  AllowedComponentProps,
  Component,
  VNode,
  VNodeArrayChildren
} from 'vue';

export const preventedScrollClass = 'cds-scroll-blocked';

export const keyNames: Record<string, KeyboardEvent['key']> = Object.freeze({
  enter: 'Enter',
  tab: 'Tab',
  delete: 'Delete',
  esc: 'Escape',
  space: ' ',
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
  end: 'End',
  home: 'Home',
  del: 'Del',
  backspace: 'Backspace',
  insert: 'Insert',
  pageup: 'PageUp',
  pagedown: 'PageDown',
  shift: 'Shift'
});

export const classesStates = Object.freeze({
  hover: 'cds-state-hover',
  focus: 'cds-state-focus',
  active: 'cds-state-active',
  visited: 'cds-state-visited',
  disabled: 'cds-state-disabled',
  checked: 'cds-state-checked',
  indeterminate: 'cds-state-indeterminate',
  invalid: 'cds-state-invalid',
  empty: 'cds-state-empty',
  loading: 'cds-state-loading',
  readonly: 'cds-state-readonly',
  dragging: 'cds-state-dragging'
});

export function keys<O extends {}>(o: O) {
  return Object.keys(o) as (keyof O)[];
}

export function has<T extends string>(obj: object, key: T[]): obj is Record<T, unknown> {
  return key.every((k) => obj.hasOwnProperty(k));
}

export function isString(value: any) {
  return typeof value === 'string';
}

export function isArray(value: any): boolean {
  return Array.isArray(value);
}

export function isSymbol(value: any) {
  return typeof value === 'symbol';
}

/**
 * Check if value is a object.
 * @param obj
 */
export function isObject(obj: any): obj is object {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

/**
 * Check if value is a Date.
 * @param value
 */
export function isDate(value: any) {
  return Object.prototype.toString.call(value) === '[object Date]';
}

/**
 * Check if value is a Window object.
 * @param obj
 */
export function isWindow(obj: any) {
  return obj !== null && obj !== undefined && obj === obj.window;
}

/**
 * Add item from array if not exists.
 * @param array
 * @param value
 */
export function addItemToArray<T>(array: T[], value: T) {
  const clone = (array || []).slice();
  if (clone.indexOf(value) === -1) {
    clone.push(value);
  }
  return clone;
}

/**
 * Remove item from array if exists.
 * @param array
 * @param value
 */
export function removeItemFromArray<T>(array: T[], value: T) {
  if (!array) return [];

  const clone = array.slice();
  const index = clone.indexOf(value);

  if (index >= 0) {
    clone.splice(index, 1);
  }

  return clone;
}

/**
 * Deep merge two objects.
 * @param source
 * @param target
 * @param arrayFn
 */
export function mergeDeep(
  source: Record<string, any> = {},
  target: Record<string, any> = {},
  arrayFn?: (a: unknown[], b: unknown[]) => unknown[]
) {
  const out: Record<string, any> = {};

  for (const key in source) {
    out[key] = source[key];
  }

  for (const key in target) {
    const sourceProperty = source[key];
    const targetProperty = target[key];

    // Only continue deep merging if
    // both properties are objects
    if (isObject(sourceProperty) && isObject(targetProperty)) {
      out[key] = mergeDeep(sourceProperty, targetProperty, arrayFn);

      continue;
    }

    if (Array.isArray(sourceProperty) && Array.isArray(targetProperty) && arrayFn) {
      out[key] = arrayFn(sourceProperty, targetProperty);

      continue;
    }

    out[key] = targetProperty;
  }

  return out;
}

/**
 * Deep compare two values to determine if they are equivalent.
 * @param a
 * @param b
 */
export function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;

  if (a instanceof Date && b instanceof Date && a.getTime() !== b.getTime()) {
    // If the values are Date, compare them as timestamps
    return false;
  }

  if (a !== Object(a) || b !== Object(b)) {
    // If the values aren't objects, they were already checked for equality
    return false;
  }

  const props = Object.keys(a);

  if (props.length !== Object.keys(b).length) {
    // Different number of props, don't bother to check
    return false;
  }

  return props.every((p) => deepEqual(a[p], b[p]));
}

/**
 * Wrap value in array if it's not already an array
 * @param value
 */
type IfAny<T, Y, N> = 0 extends 1 & T ? Y : N;
export function wrapInArray<T>(
  value: T | T[] | null | undefined
): T extends readonly any[] ? IfAny<T, T[], T> : NonNullable<T>[] {
  return value == null ? [] : Array.isArray(value) ? (value as any) : [value];
}

/**
 * Get the nested value of an object at a given path.
 * @param obj
 * @param path
 * @param fallback
 */
export function getNestedValue(obj: any, path: (string | number)[], fallback?: any): any {
  const last = path.length - 1;

  if (last < 0) return obj === undefined ? fallback : obj;

  for (let i = 0; i < last; i++) {
    if (obj == null) {
      return fallback;
    }
    obj = obj[path[i]];
  }

  if (obj == null) return fallback;

  return obj[path[last]] === undefined ? fallback : obj[path[last]];
}

/**
 * Get the value of an object at a given path.
 * @param obj
 * @param path
 * @param fallback
 */
export function getObjectValueByPath(obj: any, path: string, fallback?: any) {
  if (obj == null || !path || typeof path !== 'string') return fallback;
  if (obj[path] !== undefined) return obj[path];
  path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  path = path.replace(/^\./, ''); // strip a leading dot
  return getNestedValue(obj, path.split('.'), fallback);
}

/**
 * This function is used to get the value of a property from an item. The property can be a string, an array, a function, or a boolean.
 * If the property is a string, it will be used as a path to retrieve the value from the item.
 * If the property is an array, it will be used as a nested path to retrieve the value from the item.
 * If the property is a function, it will be called with the item and the fallback value as arguments, and its return value will be used.
 * If the property is a boolean, and it's true, the item itself will be returned.
 * If the property is null, undefined, or a boolean that's not true, the fallback value will be returned.
 * If the item is not an object, and the property is not a function, the fallback value will be returned.
 * If the property is not a string, an array, or a function, the fallback value will be returned.
 * If the value retrieved from the item is undefined, the fallback value will be returned.
 *
 * @param item - The item from which to get the property value.
 * @param property - The property to get from the item. Can be a string, an array, a function, or a boolean.
 * @param fallback - The value to return if the property is not found or its value is undefined.
 * @returns The value of the property from the item, or the fallback value.
 */
export function getPropertyFromItem(item: any, property: SelectItemKey, fallback?: any): any {
  if (property === true) return item === undefined ? fallback : item;

  if (property == null || typeof property === 'boolean') return fallback;

  if (item !== Object(item)) {
    if (typeof property !== 'function') return fallback;

    const value = property(item, fallback);

    return typeof value === 'undefined' ? fallback : value;
  }

  if (typeof property === 'string') return getObjectValueByPath(item, property, fallback);

  if (Array.isArray(property)) return getNestedValue(item, property, fallback);

  if (typeof property !== 'function') return fallback;

  const value = property(item, fallback);

  return typeof value === 'undefined' ? fallback : value;
}

/**
 * This function is used to return the scroll position of the given element.
 * @param target
 * @param top
 */
export function getScroll(target: HTMLElement | Window | Document | null, top: boolean): number {
  if (!IN_BROWSER) return 0;
  const method = top ? 'scrollTop' : 'scrollLeft';
  let result = 0;
  if (isWindow(target)) {
    result = (target as Window)[top ? 'pageYOffset' : 'pageXOffset'];
  } else if (target instanceof Document) {
    result = target.documentElement[method];
  } else if (target) {
    result = (target as HTMLElement)[method];
  }
  if (target && !isWindow(target) && typeof result !== 'number') {
    result = ((target as HTMLElement).ownerDocument || (target as Document)).documentElement?.[method];
  }
  return result;
}

/**
 * @description This function is used to return a copy of the object that is composed of the picked object properties.
 * @param obj
 * @param paths
 */
export function pick<T extends object, U extends Extract<keyof T, string>>(
  obj: T,
  paths: U[]
): [yes: MaybePick<T, U>, no: Omit<T, U>];
export function pick<T extends object, U extends Extract<keyof T, string>>(
  obj: T,
  paths: (U | RegExp)[]
): [yes: Partial<T>, no: Partial<T>] {
  const found = Object.create(null);
  const rest = Object.create(null);

  for (const key in obj) {
    if (paths.some((path) => (path instanceof RegExp ? path.test(key) : path === key))) {
      found[key] = obj[key];
    } else {
      rest[key] = obj[key];
    }
  }

  return [found, rest];
}

/**
 * @description This function is used to return a copy of the object that is composed of the picked object properties
 * excluding the specified properties.
 * @param obj
 * @param paths
 * @param exclude
 */
export function pickWithRest<T extends object, U extends Extract<keyof T, string>, E extends Extract<keyof T, string>>(
  obj: T,
  paths: U[],
  exclude?: E[]
): [yes: MaybePick<T, Exclude<U, E>>, no: Omit<T, Exclude<U, E>>];
// Array of keys or RegExp to test keys against
export function pickWithRest<T extends object, U extends Extract<keyof T, string>, E extends Extract<keyof T, string>>(
  obj: T,
  paths: (U | RegExp)[],
  exclude?: E[]
): [yes: Partial<T>, no: Partial<T>];
export function pickWithRest<T extends object, U extends Extract<keyof T, string>, E extends Extract<keyof T, string>>(
  obj: T,
  paths: (U | RegExp)[],
  exclude?: E[]
): [yes: Partial<T>, no: Partial<T>] {
  const found = Object.create(null);
  const rest = Object.create(null);

  for (const key in obj) {
    if (
      paths.some((path) => (path instanceof RegExp ? path.test(key) : path === key)) &&
      !exclude?.some((path) => path === key)
    ) {
      found[key] = obj[key];
    } else {
      rest[key] = obj[key];
    }
  }

  return [found, rest];
}

/**
 * Convert a computed ref to a record of refs.
 * The getter function must always return an object with the same keys.
 */
export function destructComputed<T extends object>(getter: ComputedGetter<T & NotAUnion<T>>): ToRefs<T>;
export function destructComputed<T extends object>(getter: ComputedGetter<T>) {
  const refs = reactive({}) as T;
  const base = computed(getter);
  watchEffect(
    () => {
      for (const key in base.value) {
        refs[key] = base.value[key];
      }
    },
    { flush: 'sync' }
  );
  return toRefs(refs);
}

/**
 * Array.includes but value can be any type
 * @param arr
 * @param val
 */
export function includes(arr: readonly any[], val: any) {
  return arr.includes(val);
}

export function eventName(propName: string) {
  return propName[2].toLowerCase() + propName.slice(3);
}

export function convertToUnit(str: number, unit?: string): string;
export function convertToUnit(str: string | number | null | undefined, unit?: string): string | undefined;
export function convertToUnit(str: string | number | null | undefined, unit = 'px'): string | undefined {
  if (str == null || str === '') {
    return undefined;
  } else if (isNaN(+str!)) {
    return String(str);
  } else if (!isFinite(+str!)) {
    return undefined;
  } else {
    return `${Number(str)}${unit}`;
  }
}

export function convertRemToPx(value: string): number {
  if (!IN_BROWSER) return 0;
  return parseFloat(value.replace('rem', '')) * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

/**
 * Clamp a value between a min and max
 * @param value
 * @param min
 * @param max
 */
export function clamp(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

export function createRange(length: number, start = 0): number[] {
  return Array.from({ length }, (v, k) => start + k);
}

/**
 * Is value an like event
 * @param key
 */
export const onRE = /^on[^a-z]/;
export const isOn = (key: string) => onRE.test(key);

export const bubblingEvents = [
  'onAfterscriptexecute',
  'onAnimationcancel',
  'onAnimationend',
  'onAnimationiteration',
  'onAnimationstart',
  'onAuxclick',
  'onBeforeinput',
  'onBeforescriptexecute',
  'onChange',
  'onClick',
  'onCompositionend',
  'onCompositionstart',
  'onCompositionupdate',
  'onContextmenu',
  'onCopy',
  'onCut',
  'onDblclick',
  'onFocusin',
  'onFocusout',
  'onFullscreenchange',
  'onFullscreenerror',
  'onGesturechange',
  'onGestureend',
  'onGesturestart',
  'onGotpointercapture',
  'onInput',
  'onKeydown',
  'onKeypress',
  'onKeyup',
  'onLostpointercapture',
  'onMousedown',
  'onMousemove',
  'onMouseout',
  'onMouseover',
  'onMouseup',
  'onMousewheel',
  'onPaste',
  'onPointercancel',
  'onPointerdown',
  'onPointerenter',
  'onPointerleave',
  'onPointermove',
  'onPointerout',
  'onPointerover',
  'onPointerup',
  'onReset',
  'onSelect',
  'onSubmit',
  'onTouchcancel',
  'onTouchend',
  'onTouchmove',
  'onTouchstart',
  'onTransitioncancel',
  'onTransitionend',
  'onTransitionrun',
  'onTransitionstart',
  'onWheel'
];

export const EventProp = [Function, Array] as PropType<EventProp>;

export function isComponentInstance(obj: any): obj is ComponentPublicInstance {
  return obj?.$el;
}

export function isFixedPosition(el?: HTMLElement) {
  while (el) {
    if (window.getComputedStyle(el).position === 'fixed') {
      return true;
    }
    el = el.offsetParent as HTMLElement;
  }
  return false;
}

export function padEnd(str: string, length: number, char = '0') {
  return str + char.repeat(Math.max(0, length - str.length));
}

export function padStart(string: number | string, targetLength: number, padString: string) {
  targetLength = targetLength >> 0;
  string = String(string);
  padString = String(padString);
  if (string.length > targetLength) {
    return String(string);
  }

  targetLength = targetLength - string.length;
  if (targetLength > padString.length) {
    padString += padString.repeat(targetLength / padString.length);
  }
  return padString.slice(0, targetLength) + String(string);
}

export function pad(n: string | number, length = 2) {
  return padStart(n, length, '0');
}

export function refElement(obj: ComponentPublicInstance<any> | HTMLElement): HTMLElement | undefined {
  if (obj && '$el' in obj) {
    const el = obj.$el as HTMLElement;

    if (el?.nodeType === Node.TEXT_NODE) {
      return el.nextElementSibling as HTMLElement;
    }

    return el;
  }

  return obj as HTMLElement;
}

export function omit<T extends object, K extends keyof T>(obj: T, fields: K[]): Omit<T, K> {
  const shallowCopy = Object.assign({}, obj);
  for (let i = 0; i < fields.length; i += 1) {
    const key = fields[i];
    delete shallowCopy[key];
  }
  return shallowCopy;
}

export function findChildrenWithProvide(
  key: InjectionKey<any> | symbol,
  vnode?: VNodeChild | { [key: string]: any }
): ComponentInternalInstance[] {
  if (!vnode || typeof vnode !== 'object') return [];

  if (Array.isArray(vnode)) {
    return vnode.map((child) => findChildrenWithProvide(key, child)).flat(1);
  } else if (Array.isArray(vnode.children)) {
    return vnode.children.map((child) => findChildrenWithProvide(key, child)).flat(1);
  } else if (vnode.component) {
    if (Object.getOwnPropertySymbols(vnode.component.provides).includes(key as symbol)) {
      return [vnode.component];
    } else if (vnode.component.subTree) {
      return findChildrenWithProvide(key, vnode.component.subTree).flat(1);
    }
  }

  return [];
}

export function focusableChildren(el: Element) {
  const targets = ['button', '[href]', 'input:not([type="hidden"])', 'select', 'textarea', '[tabindex]']
    .map((s) => `${s}:not([tabindex="-1"]):not([disabled])`)
    .join(', ');
  return [...el.querySelectorAll(targets)] as HTMLElement[];
}

/**
 * https://github.com/lodash/lodash/blob/master/partition.js
 * @param items
 * @param predicates
 */
export function partition<T>(items: T[], predicates: (item: T) => boolean): [T[], T[]] {
  const result: [T[], T[]] = [[], []];

  if (!Array.isArray(items)) return result;

  items.forEach((item) => {
    result[predicates(item) ? 0 : 1].push(item);
  });

  return result;
}

export function matchesSelector<T>(el: T | Element | undefined, selector: string): boolean | null {
  const supportSelector =
    IN_BROWSER &&
    typeof CSS !== 'undefined' &&
    typeof CSS.supports !== undefined &&
    CSS.supports(`selector(${selector})`);

  if (!supportSelector) return null;

  try {
    return !!el && (el as Element).matches(selector);
  } catch (err) {
    return null;
  }
}

/**
 * Debounce function
 * @param fn
 * @param delay
 */
export function debounce(fn: Function, delay: number) {
  let timeoutId = 0 as any;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Pluralize a word based on a number.
 * @param n
 * @param opts
 */
export function plurals(n: number, opts: string[]) {
  return opts[
    n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2
  ];
}

/**
 * Chunk a string into an array of strings.
 * @param str
 * @param size
 */
export function chunk(str: string, size = 1) {
  const chunked: string[] = [];
  let index = 0;
  while (index < str.length) {
    chunked.push(str.substr(index, size));
    index += size;
  }
  return chunked;
}

export function defer(timeout: number, cb: () => void) {
  if (!IN_BROWSER || timeout === 0) {
    cb();

    return () => {};
  }

  const timeoutId = window.setTimeout(cb, timeout);

  return () => window.clearTimeout(timeoutId);
}

export function isValidElement(element: any) {
  if (Array.isArray(element) && element.length === 1) {
    element = element[0];
  }
  return element && element.__v_isVNode && typeof element.type !== 'symbol'; // remove text node
}

export function ensureValidVNode(vnodes: VNodeArrayChildren): VNodeArrayChildren | null {
  return vnodes.some((child) => {
    if (!isVNode(child)) return true;
    if (child.type === Comment) return false;
    return child.type !== Fragment || ensureValidVNode(child.children as VNodeArrayChildren);
  })
    ? vnodes
    : null;
}

export function noop() {}

/**
 * Types
 */
export type MaybeRef<T> = T | Ref<T>;

declare type VNodeChildAtom = VNode | string | number | boolean | null | undefined | void;
export type VueNode = VNodeChildAtom | VNodeChildAtom[];

export type SelectItemKey = TItemKey;

export type MaybePick<T extends object, U extends Extract<keyof T, string>> = Record<string, unknown> extends T
  ? Partial<Pick<T, U>>
  : Pick<T, U>;

export type EventProp<T = (...args: any[]) => any> = T;
// Only allow a single return type

type NotAUnion<T> = [T] extends [infer U] ? _NotAUnion<U, U> : never;
type _NotAUnion<T, U> = U extends any ? ([T] extends [U] ? unknown : never) : never;

export type ComponentProps<C extends Component> = C extends new (...args: any) => any
  ? Omit<InstanceType<C>['$props'], keyof VNodeProps | keyof AllowedComponentProps>
  : never;

/**
 * Maybe it's a ref, or a plain value, or a getter function
 *
 * ```ts
 * type MaybeRefOrGetter<T> = (() => T) | T | Ref<T> | ComputedRef<T>
 * ```
 */
export type MaybeRefOrGetter<T> = MaybeRef<T> | (() => T);

export type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;
